import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { orderService } from "../services/api";
import { FiPackage, FiTruck, FiCheck, FiShoppingCart } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

const cn = (...cls) => cls.filter(Boolean).join(" ");

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getItemUnitPrice = (item) => {
  const saved = toNumber(item?.unit_price);
  if (saved > 0) return saved;

  const promo = toNumber(item?.promotional_price);
  if (promo > 0) return promo;

  const sell = toNumber(item?.selling_price);
  if (sell > 0) return sell;

  return toNumber(item?.price);
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, getCartTotal, clearCart } = useCart();

  // ALWAYS force top (works on refresh too)
  useLayoutEffect(() => {
    const jumpTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    jumpTop();
    // second tick to defeat browser scroll restoration on reload
    requestAnimationFrame(jumpTop);
  }, [location.key]);

  const [loading, setLoading] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    street_address: "",
    notes: "",
  });

  const translations = {
    checkout: "Finaliser la commande",
    orderSummary: "Résumé de la commande",
    subtotal: "Sous-total",
    delivery: "Livraison",
    total: "Total",
    fullName: "Nom complet *",
    phone: "Téléphone *",
    email: "Email (optionnel)",
    address: "Adresse complète *",
    notes: "Notes (optionnel)",
    placeOrder: "Confirmer la commande",
    placingOrder: "Création de votre commande...",
    errorEmptyCart: "Panier vide",
    backToShop: "Retour à la boutique",
  };

  useEffect(() => {
    if (cartItems.length === 0 && !orderSubmitted) {
      navigate("/cart");
    }
  }, [cartItems.length, navigate, orderSubmitted]);

  const subtotal = useMemo(() => getCartTotal(), [getCartTotal]);
  const deliveryPrice = 0;
  const total = subtotal + deliveryPrice;

  const itemsCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (toNumber(item.quantity) || 1), 0),
    [cartItems]
  );

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.client_name || !form.client_phone || !form.street_address) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const products = cartItems.map((item) => {
      const unit = getItemUnitPrice(item);
      const qty = parseInt(item.quantity || 1, 10);

      return {
        id: parseInt(item.id, 10),
        name: String(item.name || "Produit"),
        price: parseFloat(unit || 0),
        quantity: qty,
        discount_label: item.discount_label || "",
        color: item.selectedColor || item.color_name || item.color || "",
      };
    });

    const orderData = {
      client_name: form.client_name.trim(),
      client_phone: form.client_phone.trim(),
      client_email: (form.client_email || "").trim(),
      street_address: form.street_address.trim(),
      subtotal: total,
      delivery_price: deliveryPrice,
      delivery_company_id: 1,
      notes: (form.notes || "").trim(),
      products: JSON.stringify(products),
    };

    try {
      setLoading(true);
      const res = await orderService.createOrder(orderData);

      setOrderSubmitted(true);

      navigate("/thank-you", {
        state: {
          orderNumber: res?.order_number,
          clientName: form.client_name,
          clientPhone: form.client_phone,
          total,
          itemsCount,
          items: cartItems,
          timestamp: new Date().toLocaleString("fr-FR"),
        },
      });

      clearCart();
    } catch (err) {
      setOrderSubmitted(false);

      if (err.response?.status === 422) {
        const errors = err.response?.data?.errors;
        if (errors) {
          const errorMessages = Object.values(errors).flat().join(", ");
          setError(`Données invalides: ${errorMessages}`);
        } else {
          setError("Données invalides. Vérifiez vos informations");
        }
      } else {
        setError("Erreur serveur. Réessayez plus tard");
      }
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderSubmitted) {
    return (
      <div className="min-h-screen bg-white px-4 py-16 text-center" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <Helmet>
          <title>Checkout | ORIMA</title>
        </Helmet>

        <FiPackage className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{translations.errorEmptyCart}</h2>
        <button
          onClick={() => navigate("/shop")}
          className="mt-4 bg-yellow-200 text-gray-900 px-6 py-3 rounded-2xl font-extrabold hover:bg-yellow-300 transition"
        >
          {translations.backToShop}
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{translations.checkout} | ORIMA</title>
        <meta name="description" content="Finaliser votre commande ORIMA. Livraison gratuite au Maroc." />
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{translations.checkout}</h1>
            <p className="mt-1 text-sm text-gray-600">Vérifiez vos informations puis confirmez votre commande.</p>
          </div>

          <div className="grid grid-cols-1 gap-7 lg:grid-cols-12">
            {/* LEFT */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-5">Informations de livraison</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{translations.fullName}</label>
                    <input
                      type="text"
                      name="client_name"
                      value={form.client_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Votre nom complet"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{translations.phone}</label>
                    <input
                      type="tel"
                      name="client_phone"
                      value={form.client_phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="+212 6XX XXX XXX"
                    />
                  </div>

                  <div className="hidden">
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{translations.email}</label>
                    <input
                      type="email"
                      name="client_email"
                      value={form.client_email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="exemple@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{translations.address}</label>
                    <textarea
                      name="street_address"
                      value={form.street_address}
                      onChange={handleChange}
                      required
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Adresse complète de livraison"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{translations.notes}</label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Instructions spéciales..."
                    />
                  </div>

                  {error && (
                    <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      "w-full rounded-2xl px-6 py-4 text-lg font-extrabold flex items-center justify-center gap-2 transition-transform active:scale-95",
                      loading ? "bg-gray-200 text-gray-600 cursor-not-allowed" : "bg-yellow-200 text-gray-900 hover:bg-yellow-300 btn-attire-yellow"
                    )}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
                        {translations.placingOrder}
                      </>
                    ) : (
                      <>
                        <FiCheck className="w-5 h-5" />
                        {translations.placeOrder}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* RIGHT */}
            <div className="lg:col-span-5 lg:sticky lg:top-6 lg:self-start">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">{translations.orderSummary}</h2>
                  <div className="text-xs font-semibold text-gray-600">{itemsCount} article(s)</div>
                </div>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => {
                    const unit = getItemUnitPrice(item);
                    const qty = parseInt(item.quantity || 1, 10);
                    const lineTotal = unit * qty;

                    const img = item.main_image?.startsWith("http")
                      ? item.main_image
                      : `https://pro-dash.orima.ma/${String(item.main_image || "").replace(/^\/+/, "")}`;

                    return (
                      <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100">
                        <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 flex-shrink-0">
                          <img
                            src={img}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/150?text=No+Image";
                            }}
                          />
                        </div>

                        <div className="flex-1">
                          <h3 className="font-semibold text-sm text-gray-900 line-clamp-2">{item.name}</h3>
                          <div className="mt-1 text-xs text-gray-600">
                            Qté: <span className="font-semibold">{qty}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">Unité: {unit.toFixed(2)} MAD</div>
                          <div className="mt-1 text-sm font-extrabold text-gray-900">{lineTotal.toFixed(2)} MAD</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-gray-700">
                    <span>{translations.subtotal}</span>
                    <span className="font-semibold">{subtotal.toFixed(2)} MAD</span>
                  </div>

                  <div className="flex justify-between text-gray-700">
                    <span className="flex items-center gap-2">
                      <FiTruck className="w-4 h-4" />
                      {translations.delivery}
                    </span>
                    <span className="font-semibold text-green-600">Gratuite</span>
                  </div>

                  <div className="flex justify-between text-xl font-extrabold text-gray-900 pt-3 border-t border-gray-100">
                    <span>{translations.total}</span>
                    <span>{total.toFixed(2)} MAD</span>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl bg-rose-500 text-white px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-semibold">
                    <FiTruck className="w-5 h-5" />
                    Livraison gratuite
                  </div>
                  <div className="text-xs text-white/90">1 à 2 jours</div>
                </div>

                <button
                  onClick={() => navigate("/shop")}
                  className="mt-4 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-800 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                >
                  <FiShoppingCart className="w-4 h-4" />
                  Continuer vos achats
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes attractYellow {
            0%, 100% { transform: scale(1); box-shadow: 0 10px 35px rgba(0,0,0,0.12); }
            50% { transform: scale(1.03); box-shadow: 0 18px 55px rgba(0,0,0,0.22); }
          }
          .btn-attire-yellow { animation: attractYellow 1.6s ease-in-out infinite; }
        `}</style>
      </div>
    </>
  );
};

export default Checkout;
